import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { messagesApi, exercisesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  RefreshCw,
  Reply,
  User,
  Users,
  Clock,
  AlertCircle,
  CheckCircle,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  simcel: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  observer: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  participant: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const priorityColors = {
  normal: 'bg-muted text-foreground dark:bg-card dark:text-foreground',
  high: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function MessagesPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  // Compose form
  const [messageType, setMessageType] = useState('individual'); // individual, role, broadcast
  const [newMessage, setNewMessage] = useState({
    exercise_id: '',
    recipient_id: '',
    recipient_role: '',
    subject: '',
    content: '',
    priority: 'normal',
    is_group: false,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [messagesData, exercisesData] = await Promise.all([
        messagesApi.getAll(selectedExercise === 'all' ? null : selectedExercise, showUnreadOnly),
        exercisesApi.getAll(),
      ]);
      setMessages(messagesData);
      setExercises(exercisesData);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (exerciseId) => {
    if (!exerciseId) {
      setUsers([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/messages/users?exercise_id=${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedExercise, showUnreadOnly]);

  useEffect(() => {
    if (newMessage.exercise_id) {
      loadUsers(newMessage.exercise_id);
    }
  }, [newMessage.exercise_id]);

  const handleSendMessage = async () => {
    if (!newMessage.exercise_id || !newMessage.subject || !newMessage.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (messageType === 'individual' && !newMessage.recipient_id) {
      toast.error('Please select a recipient');
      return;
    }

    setSending(true);
    try {
      const payload = {
        exercise_id: newMessage.exercise_id,
        subject: newMessage.subject,
        content: newMessage.content,
        priority: newMessage.priority,
        is_group: messageType !== 'individual',
      };

      if (messageType === 'individual') {
        payload.recipient_id = newMessage.recipient_id;
      } else if (messageType === 'role') {
        payload.recipient_role = newMessage.recipient_role;
      }
      // broadcast = no recipient_id or recipient_role

      await messagesApi.send(payload);
      toast.success('Message sent successfully');
      setComposeOpen(false);
      setNewMessage({ exercise_id: '', recipient_id: '', recipient_role: '', subject: '', content: '', priority: 'normal', is_group: false });
      setMessageType('individual');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSending(true);
    try {
      await messagesApi.reply(viewMessage.message_id, replyContent);
      toast.success('Reply sent');
      setReplyContent('');
      setViewMessage(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const openMessage = async (msg) => {
    try {
      const fullMessage = await messagesApi.get(msg.message_id);
      setViewMessage(fullMessage);
      loadData();
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const getRecipientDisplay = (msg) => {
    if (msg.is_own) {
      if (msg.recipient_name) return `To: ${msg.recipient_name}`;
      if (msg.recipient_role) return `To: ${msg.recipient_role}s`;
      return 'To: Everyone';
    }
    return msg.sender_name;
  };

  return (
    <DashboardLayout title="Messages">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Messages</h1>
            <p className="text-muted-foreground">Communicate with team members</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setComposeOpen(true)} data-testid="compose-message-btn">
              <Send className="w-4 h-4 mr-2" />
              Compose
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Select value={selectedExercise} onValueChange={setSelectedExercise}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Exercises" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Exercises</SelectItem>
                  {exercises.map((ex) => (
                    <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                      {ex.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={showUnreadOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              >
                {showUnreadOnly ? <CheckCircle className="w-4 h-4 mr-2" /> : <AlertCircle className="w-4 h-4 mr-2" />}
                Unread Only
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Inbox ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No messages found</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
                        !msg.is_read && !msg.is_own ? 'bg-primary/5 border-primary/20' : ''
                      }`}
                      onClick={() => openMessage(msg)}
                      data-testid={`message-${msg.message_id}`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {!msg.is_read && !msg.is_own && (
                              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            )}
                            <span className="font-medium truncate">
                              {getRecipientDisplay(msg)}
                            </span>
                            <Badge className={roleColors[msg.sender_role] || roleColors.participant} variant="outline">
                              {msg.sender_role}
                            </Badge>
                            {msg.is_group && (
                              <Badge variant="secondary">
                                <Users className="w-3 h-3 mr-1" />
                                Group
                              </Badge>
                            )}
                            {msg.priority !== 'normal' && (
                              <Badge className={priorityColors[msg.priority]}>
                                {msg.priority}
                              </Badge>
                            )}
                          </div>
                          <p className="font-semibold truncate">{msg.subject}</p>
                          <p className="text-sm text-muted-foreground truncate">{msg.content}</p>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(msg.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compose Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Exercise *</Label>
              <Select
                value={newMessage.exercise_id}
                onValueChange={(v) => setNewMessage({ ...newMessage, exercise_id: v, recipient_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select exercise" />
                </SelectTrigger>
                <SelectContent>
                  {exercises.map((ex) => (
                    <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                      {ex.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newMessage.exercise_id && (
              <>
                <div className="space-y-3">
                  <Label>Send To</Label>
                  <RadioGroup value={messageType} onValueChange={setMessageType} className="flex flex-col gap-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="individual" id="individual" />
                      <Label htmlFor="individual" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Individual Person
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="role" id="role" />
                      <Label htmlFor="role" className="flex items-center gap-2 cursor-pointer">
                        <Users className="w-4 h-4" />
                        All Users of a Role
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="broadcast" id="broadcast" />
                      <Label htmlFor="broadcast" className="flex items-center gap-2 cursor-pointer">
                        <MessageSquare className="w-4 h-4" />
                        Everyone in Exercise
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {messageType === 'individual' && (
                  <div className="space-y-2">
                    <Label>Select Person *</Label>
                    <Select
                      value={newMessage.recipient_id}
                      onValueChange={(v) => setNewMessage({ ...newMessage, recipient_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose recipient" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>
                            <div className="flex items-center gap-2">
                              <span>{u.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {u.roles?.[0]}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {messageType === 'role' && (
                  <div className="space-y-2">
                    <Label>Select Role</Label>
                    <Select
                      value={newMessage.recipient_role}
                      onValueChange={(v) => setNewMessage({ ...newMessage, recipient_role: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Super Admins</SelectItem>
                        <SelectItem value="simcel">Simcell Controllers</SelectItem>
                        <SelectItem value="observer">Observers</SelectItem>
                        <SelectItem value="participant">Participants</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={newMessage.priority}
                onValueChange={(v) => setNewMessage({ ...newMessage, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                placeholder="Message subject"
              />
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={newMessage.content}
                onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                placeholder="Type your message..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} disabled={sending}>
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Message Dialog */}
      <Dialog open={!!viewMessage} onOpenChange={() => setViewMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewMessage?.subject}</DialogTitle>
          </DialogHeader>
          {viewMessage && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <User className="w-4 h-4" />
                <span className="font-medium">{viewMessage.sender_name}</span>
                <Badge className={roleColors[viewMessage.sender_role]} variant="outline">
                  {viewMessage.sender_role}
                </Badge>
                {viewMessage.is_group && (
                  <Badge variant="secondary">
                    <Users className="w-3 h-3 mr-1" />
                    Group
                  </Badge>
                )}
                {viewMessage.priority !== 'normal' && (
                  <Badge className={priorityColors[viewMessage.priority]}>
                    {viewMessage.priority}
                  </Badge>
                )}
              </div>
              {viewMessage.recipient_name && (
                <div className="text-xs text-muted-foreground">
                  To: {viewMessage.recipient_name}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                <Clock className="w-3 h-3 inline mr-1" />
                {new Date(viewMessage.created_at).toLocaleString()}
              </div>
              <div className="bg-muted/50 rounded-lg p-4 whitespace-pre-wrap">
                {viewMessage.content}
              </div>

              {/* Reply Section */}
              {!viewMessage.is_own && (
                <div className="space-y-2 pt-4 border-t">
                  <Label className="flex items-center gap-2">
                    <Reply className="w-4 h-4" />
                    Reply
                  </Label>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                  />
                  <Button onClick={handleReply} disabled={sending} size="sm">
                    {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
